import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PostCard from "../components/PostCard";
import { Badge } from "../components/ui/badge";
import { CalendarBlank, MapPin, EnvelopeSimple } from "@phosphor-icons/react";

export default function Profile() {
    const { user } = useAuth();
    const [myPosts, setMyPosts] = useState([]);
    const [savedPosts, setSavedPosts] = useState([]);

    useEffect(() => {
        if (!user) return;
        api.get(`/users/${user.id}/posts`).then((r) => setMyPosts(r.data));
        api.get(`/posts/saved/list`).then((r) => setSavedPosts(r.data));
    }, [user]);

    if (!user) return null;
    return (
        <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-4xl mx-auto" data-testid="profile-page">
            <header className="flex items-center gap-5 mb-10">
                <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-[#6C63FF] to-[#FF8A00] grid place-items-center text-white font-display font-black text-3xl">
                    {(user.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div>
                    <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter brand-text">{user.name}</h1>
                    <div className="text-muted-foreground flex flex-wrap items-center gap-3 mt-1 text-sm">
                        <span className="flex items-center gap-1"><EnvelopeSimple size={14} weight="duotone" />{user.email}</span>
                        <span className="flex items-center gap-1"><MapPin size={14} weight="duotone" />{user.country}</span>
                        <span className="flex items-center gap-1"><CalendarBlank size={14} weight="duotone" />Joined {new Date(user.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="mt-2 flex gap-2">
                        {(() => {
                            // Render a tier badge with friendly labels rather
                            // than the raw subscription string (which can be
                            // "pro_local" / "pro_global" / "pro" / "free").
                            const sub = (user.subscription || "free").toLowerCase();
                            const isProGlobal = sub === "pro_global" || sub === "pro";
                            const isProLocal  = sub === "pro_local";
                            const label = isProGlobal ? "PRO · GLOBAL"
                                : isProLocal ? "PRO · LOCAL"
                                : sub.toUpperCase();
                            const cls = isProGlobal ? "bg-[#FF8A00] text-white"
                                : isProLocal ? "bg-[#6C63FF] text-white"
                                : "";
                            return <Badge className={cls} data-testid="profile-tier-badge">{label}</Badge>;
                        })()}
                        {user.role === "admin" && <Badge className="bg-[#6C63FF] text-white">ADMIN</Badge>}
                    </div>
                </div>
            </header>

            <section className="mb-10">
                <h2 className="font-display font-bold text-2xl mb-4">Saved ideas ({savedPosts.length})</h2>
                {savedPosts.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground">No saved ideas yet.</div>
                : <div className="grid gap-4">{savedPosts.map(p => <PostCard key={p.id} post={p} />)}</div>}
            </section>

            <section>
                <h2 className="font-display font-bold text-2xl mb-4">My ideas ({myPosts.length})</h2>
                {myPosts.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground">No ideas posted yet.</div>
                : <div className="grid gap-4">{myPosts.map(p => (
                    <PostCard key={p.id} post={p}
                              onDelete={(id) => setMyPosts(prev => prev.filter(x => x.id !== id))} />
                ))}</div>}
            </section>
        </div>
    );
}
